import { DeliveryListFilter, PendingDeliveryScheduleFormData } from "../../types/delivery";
import DeliveryScheduleList from "./deliveryScheduleList.comp";
import deliveryCore from "../../core/delivery.core";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "../../core/translation.core";
import { useForm, UseFormReturn } from "react-hook-form";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import DeliveriesFilter from "./deliveriesFilter";
import { addDays, format } from "date-fns";
import { useRouter } from "next/router";
import ProcessingOrders from "./processingOrders";

export default function PendingDeliveries() {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    })
  );

  const filterMethods = useDeliveryListFilter();
  const methods = useForm<PendingDeliveryScheduleFormData>({ defaultValues: { orderItemIds: [] } });

  async function onSubmit(data: PendingDeliveryScheduleFormData) {
    deliveryCore
      .createSchedule(data)
      .then((_) => {
        queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
        queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
        toast.success(t(msg.import.success));
      })
      .catch((_) => {
        toast.error(t(msg.errors.contactAdmin));
      });
  }

  return (
    <DndContext
      onDragEnd={(event) => {
        const { shipDate, vehicleId } = event?.over?.data.current ?? {};
        const { orderItemIds = [] } = event.active.data.current ?? {};

        if (!shipDate || !vehicleId || orderItemIds.length < 1) return;
        onSubmit({ orderItemIds, shipDate, vehicleId });
      }}
      sensors={sensors}
    >
      <DeliveriesFilter methods={filterMethods} />
      <Content methods={methods} filterMethods={filterMethods} />
    </DndContext>
  );
}

function useDeliveryListFilter(): UseFormReturn<DeliveryListFilter> {
  const { query } = useRouter();

  return useForm<DeliveryListFilter>({
    defaultValues: {
      dateOption: "last7Days",
      regions: [],
      districts: [],
      subDistricts: [],
      productIds: [],
      warehouseIds: [],
      supplierIds: [],
      customerIds: [],
      shipOptionIds: [],
      date: {
        from: format(addDays(new Date(), -7), "yyyy-MM-dd"),
        to: format(new Date(), "yyyy-MM-dd"),
      },
      paging: {
        page: Number(query.page) || 1,
        size: 50,
      },
    },
  });
}

function Content({
  methods,
  filterMethods,
}: {
  methods: UseFormReturn<PendingDeliveryScheduleFormData>;
  filterMethods: UseFormReturn<DeliveryListFilter>;
}) {
  return (
    <div className="my-4">
      <section className="flex flex-col lg:flex-row gap-4 text-sm">
        <ProcessingOrders filterMethods={filterMethods} />
        <section data-type="section" className="basis-2/5 order-1 lg:order-2 max-h-[calc(100vh-17rem)] overflow-y-auto">
          <DeliveryScheduleList methods={methods} />
        </section>
      </section>
    </div>
  );
}
